import {
  PlanType,
  StatusType,
  type DeviceStatus,
  type DeviceType,
  type PlanResponse,
  type ProgramType,
} from "../../state/api.types";
import { isOneOf } from "../../utilities/enum";
import { getStatusDisplay, ORDERED_STATUS_TYPES } from "./accountStatusHelpers";
import {
  type DeviceStatusFilter,
  getDeviceProgramLabel,
  toDeviceStatusParams,
  toDeviceTypes,
  getDeviceStatusDisplay,
  parseDevicePrograms,
  parseDeviceStatuses,
} from "./deviceFilterHelpers";

/**
 * How far ahead the expirations toggle looks. The endpoint takes any day count;
 * 90 days is the window in which renewal offers go out for a standard term, so
 * it holds the accounts actually being asked to renew.
 */
export const EXPIRING_SOON_DAYS = 90;

export type AccountFilters = {
  planGroupUUIDs: string[];
  statuses: StatusType[];
  fallbackPlanOnly: boolean;
  devicePrograms: ProgramType[];
  deviceStatuses: DeviceStatusFilter[];
  upcomingExpirationsOnly: boolean;
};

export const EMPTY_ACCOUNT_FILTERS: AccountFilters = {
  planGroupUUIDs: [],
  statuses: [],
  fallbackPlanOnly: false,
  devicePrograms: [],
  deviceStatuses: [],
  upcomingExpirationsOnly: false,
};

/** The query params a set of filters becomes on the accounts endpoints. */
export type AccountFilterParams = {
  filter: StatusType[];
  plan_group_uuid: string[];
  plan_type: PlanType | undefined;
  include_fallback_plans: true;
  device_type: DeviceType[];
  device_status?: DeviceStatus[];
  missing_program_device?: boolean;
  expiring_in_days?: number;
};

export function toAccountFilterParams(
  filters: AccountFilters,
): AccountFilterParams {
  return {
    filter: filters.statuses,
    plan_group_uuid: filters.planGroupUUIDs,
    plan_type: filters.fallbackPlanOnly ? PlanType.Fallback : undefined,
    include_fallback_plans: true,
    device_type: toDeviceTypes(filters.devicePrograms),
    ...toDeviceStatusParams(filters.deviceStatuses),
    expiring_in_days: filters.upcomingExpirationsOnly
      ? EXPIRING_SOON_DAYS
      : undefined,
  };
}

const PLAN_PILL_PREFIX = "plan:";
const STATUS_PILL_PREFIX = "status:";
const DEVICE_PROGRAM_PILL_PREFIX = "deviceProgram:";
const DEVICE_STATUS_PILL_PREFIX = "deviceStatus:";
const FALLBACK_PILL_KEY = "fallbackPlanOnly";
const UPCOMING_EXPIRATIONS_PILL_KEY = "upcomingExpirationsOnly";

export function parseStatuses(values: string[]): StatusType[] {
  return values.filter((value) => isOneOf(ORDERED_STATUS_TYPES, value));
}

const sameIds = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

/** Selections are kept in option order, so a positional comparison is enough. */
export function areFiltersEqual(a: AccountFilters, b: AccountFilters): boolean {
  return (
    a.fallbackPlanOnly === b.fallbackPlanOnly &&
    a.upcomingExpirationsOnly === b.upcomingExpirationsOnly &&
    sameIds(a.planGroupUUIDs, b.planGroupUUIDs) &&
    sameIds(a.statuses, b.statuses) &&
    sameIds(a.devicePrograms, b.devicePrograms) &&
    sameIds(a.deviceStatuses, b.deviceStatuses)
  );
}

export function hasActiveSlideoutFilters(filters: AccountFilters): boolean {
  return (
    filters.planGroupUUIDs.length > 0 ||
    filters.statuses.length > 0 ||
    filters.devicePrograms.length > 0 ||
    filters.deviceStatuses.length > 0 ||
    filters.upcomingExpirationsOnly ||
    filters.fallbackPlanOnly
  );
}

export function getActiveFilterPills(
  filters: AccountFilters,
  plans: PlanResponse[],
): { key: string; label: string }[] {
  const pills: { key: string; label: string }[] = [];

  for (const planGroupUUID of filters.planGroupUUIDs) {
    const plan = plans.find((p) => p.uuid === planGroupUUID);
    pills.push({
      key: `${PLAN_PILL_PREFIX}${planGroupUUID}`,
      label: plan?.name ?? "Selected plan",
    });
  }

  for (const status of filters.statuses) {
    pills.push({
      key: `${STATUS_PILL_PREFIX}${status}`,
      label: getStatusDisplay(status).label,
    });
  }

  if (filters.upcomingExpirationsOnly) {
    pills.push({
      key: UPCOMING_EXPIRATIONS_PILL_KEY,
      label: "Upcoming expirations",
    });
  }

  if (filters.fallbackPlanOnly) {
    pills.push({ key: FALLBACK_PILL_KEY, label: "Fallback plan" });
  }

  for (const programType of filters.devicePrograms) {
    pills.push({
      key: `${DEVICE_PROGRAM_PILL_PREFIX}${programType}`,
      label: getDeviceProgramLabel(programType),
    });
  }

  for (const deviceStatus of filters.deviceStatuses) {
    // Prefixed: an account status of the same name can be filtered alongside.
    pills.push({
      key: `${DEVICE_STATUS_PILL_PREFIX}${deviceStatus}`,
      label: `Device ${getDeviceStatusDisplay(deviceStatus).label.toLowerCase()}`,
    });
  }

  return pills;
}

export function removeFilter(
  filters: AccountFilters,
  key: string,
): AccountFilters {
  if (key.startsWith(PLAN_PILL_PREFIX)) {
    const planGroupUUID = key.slice(PLAN_PILL_PREFIX.length);
    return {
      ...filters,
      planGroupUUIDs: filters.planGroupUUIDs.filter(
        (uuid) => uuid !== planGroupUUID,
      ),
    };
  }

  if (key.startsWith(STATUS_PILL_PREFIX)) {
    const [status] = parseStatuses([key.slice(STATUS_PILL_PREFIX.length)]);
    return {
      ...filters,
      statuses: filters.statuses.filter((s) => s !== status),
    };
  }

  if (key.startsWith(DEVICE_PROGRAM_PILL_PREFIX)) {
    const [programType] = parseDevicePrograms([
      key.slice(DEVICE_PROGRAM_PILL_PREFIX.length),
    ]);
    return {
      ...filters,
      devicePrograms: filters.devicePrograms.filter((p) => p !== programType),
    };
  }

  if (key.startsWith(DEVICE_STATUS_PILL_PREFIX)) {
    const [deviceStatus] = parseDeviceStatuses([
      key.slice(DEVICE_STATUS_PILL_PREFIX.length),
    ]);
    return {
      ...filters,
      deviceStatuses: filters.deviceStatuses.filter((s) => s !== deviceStatus),
    };
  }

  if (key === FALLBACK_PILL_KEY) {
    return { ...filters, fallbackPlanOnly: false };
  }

  if (key === UPCOMING_EXPIRATIONS_PILL_KEY) {
    return { ...filters, upcomingExpirationsOnly: false };
  }

  return filters;
}
